import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Mail, MessageCircle, UserPlus, StickyNote } from "lucide-react";

const STATUSES = ["New", "Assigned", "In Progress", "Quoted", "Customer Responded", "Confirmed", "Closed"];
const STATUS_STYLE = {
  New: "bg-terra/15 text-terra",
  "In Progress": "bg-gold/15 text-gold",
  Quoted: "bg-ink/10 text-ink",
  Confirmed: "bg-emerald2/10 text-emerald2",
};

const ROWS = [
  { id: "INQ-00482", name: "Rahul Sharma", dest: "Maldives", date: "Feb 12", pax: "2+1", status: "New", fresh: true },
  { id: "INQ-00481", name: "Sarah Chen", dest: "Thailand", date: "Mar 03", pax: "4", status: "Quoted" },
  { id: "INQ-00480", name: "Ahmed Al Farsi", dest: "Dubai", date: "Apr 12", pax: "2", status: "In Progress" },
];

export default function SimInbox() {
  const [openId, setOpenId] = useState(null);
  const open = ROWS.find((r) => r.id === openId);

  return (
    <div data-testid="sim-inbox">
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {STATUSES.map((s) => (
          <span key={s} className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${STATUS_STYLE[s] || "bg-parch text-stone2"}`}>{s}</span>
        ))}
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-stone2">simulated CRM · sample data</span>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-sand bg-white">
        <table className="w-full min-w-[560px] text-left text-sm">
          <thead>
            <tr className="border-b border-sand font-mono text-[10px] uppercase tracking-[0.16em] text-stone2">
              {["Inquiry", "Customer", "Destination", "Travel date", "Travelers", "Status"].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-sand/70">
            {ROWS.map((r) => (
              <tr
                key={r.id}
                onClick={() => setOpenId(openId === r.id ? null : r.id)}
                data-testid={`sim-inbox-row-${r.id.toLowerCase()}`}
                className={`cursor-pointer transition-colors duration-200 hover:bg-paper ${openId === r.id ? "bg-terra/5" : ""}`}
              >
                <td className="px-4 py-3.5 font-mono text-xs font-bold text-ink">
                  {r.id}
                  {r.fresh && <span className="ml-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-terra align-middle" />}
                </td>
                <td className="px-4 py-3.5 font-semibold text-ink">{r.name}</td>
                <td className="px-4 py-3.5 text-stone2">{r.dest}</td>
                <td className="px-4 py-3.5 text-stone2">{r.date}</td>
                <td className="px-4 py-3.5 text-stone2">{r.pax}</td>
                <td className="px-4 py-3.5">
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-4 grid gap-3 rounded-2xl border border-sand bg-white p-4 sm:grid-cols-3" data-testid="sim-inbox-workspace">
              <div className="rounded-xl bg-paper p-4">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Customer</p>
                <p className="mt-2 font-serif text-lg text-ink">{open.name}</p>
                <p className="text-xs text-stone2">{open.dest === "Dubai" ? "UAE" : "India"}</p>
                <div className="mt-3 flex gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-sand text-stone2"><MessageCircle className="h-4 w-4" /></span>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-sand text-stone2"><Mail className="h-4 w-4" /></span>
                </div>
              </div>
              <div className="rounded-xl bg-paper p-4">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Conversation</p>
                <div className="mt-2 space-y-2 text-xs">
                  <p className="rounded-lg rounded-tl-none bg-white px-3 py-2 text-ink shadow-sm">"We would prefer a water villa and private transfers."</p>
                  <p className="ml-6 rounded-lg rounded-tr-none bg-ink px-3 py-2 text-paper">"Absolutely. I'll update the proposal."</p>
                </div>
              </div>
              <div className="rounded-xl bg-paper p-4">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Trip</p>
                <p className="mt-2 text-sm font-bold text-ink">{open.dest} · {open.date}</p>
                <p className="text-xs text-stone2">{open.pax} travelers · 5 Star · Half Board</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <a href="#jstep-7" onClick={(e) => { e.preventDefault(); document.querySelector("#jstep-7")?.scrollIntoView({ behavior: "smooth" }); }}
                    data-testid="sim-inbox-create-quote-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-terra px-3.5 py-2 text-[11px] font-bold text-ink transition-colors hover:bg-ink hover:text-paper">
                    Create Quote <ArrowRight className="h-3 w-3" />
                  </a>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-sand px-3 py-2 text-[11px] font-semibold text-stone2"><UserPlus className="h-3 w-3" /> Assign</span>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-sand px-3 py-2 text-[11px] font-semibold text-stone2"><StickyNote className="h-3 w-3" /> Note</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!open && <p className="mt-3 text-center text-xs text-stone2">Click an inquiry to open its workspace.</p>}
    </div>
  );
}
